import { useQuery } from "@tanstack/react-query";
import type { Moment } from "moment-timezone";
import type {
	IncentiveRow,
	IncentiveReportSummary,
} from "../../models/IncentiveRow";
import type {
	IncentiveRowWithEventSnapshots,
	ShiftIncentiveEventSnapshotRow,
} from "../../models/ShiftIncentiveEvent";
import { mapIncentiveRowsWithEventSnapshots } from "../../transforms/mapIncentiveRowsWithEventSnapshots";
import { getIncentiveReport } from "../../repositories/IncentiveRepository";

interface IncentiveReportData {
	rows: IncentiveRow[];
	summary: IncentiveReportSummary;
	eventSnapshots: ShiftIncentiveEventSnapshotRow[];
}

interface IncentiveReportViewData extends IncentiveReportData {
	rows: IncentiveRowWithEventSnapshots[];
}

const selectIncentiveReportData = (
	data: IncentiveReportData,
): IncentiveReportViewData => ({
	...data,
	rows: mapIncentiveRowsWithEventSnapshots(data.rows, data.eventSnapshots),
});

export const useIncentiveReportQuery = (
	startDate: Moment,
	endDate: Moment,
	crews: string[],
	shift: string | null,
	assetId: number,
	enabled = true,
) => {
	const startStr = startDate.format("YYYY-MM-DD");
	const endStr = endDate.format("YYYY-MM-DD");
	const crewKey = [...crews].sort().join("|");

	return useQuery<IncentiveReportData, Error, IncentiveReportViewData>({
		queryKey: ["incentive-report", assetId, startStr, endStr, crewKey, shift],
		queryFn: ({ signal }) =>
			getIncentiveReport(startStr, endStr, crews, shift, assetId, signal),
		enabled,
		select: selectIncentiveReportData,
	});
};
